class ArticleTableOfContents extends HTMLElement {
  #desktopMediaQuery = window.matchMedia('(min-width: 1150px)');
  #handleViewportChange;

  connectedCallback() {
    const list = this.querySelector('[data-toc-list]');
    const prose = document.getElementById('article-prose');
    if (!list || !prose) return;

    const headings = [...prose.querySelectorAll('h2, h3')];
    if (headings.length < 2) {
      this.hidden = true;
      return;
    }

    this.activeLink = null;
    this.headingMap = new Map();
    this.groupElements = [];
    this.closeOthers = this.hasAttribute('data-close-others');

    headings.forEach((heading, i) => {
      heading.id ||= `section-${i + 1}`;
    });

    const caretHtml = this.#getCaretHtml();
    const items = this.#groupHeadings(headings);
    const desktop = this.#desktopMediaQuery.matches;

    items.forEach((item) => {
      if (item.isGroup) {
        list.appendChild(this.#renderGroup(item, caretHtml, desktop));
      } else {
        list.appendChild(this.#renderPlainItem(item.heading));
      }
    });

    this.classList.toggle('article-toc--desktop', desktop);
    this.classList.toggle('article-toc--mobile', !desktop);

    this.#handleViewportChange = () => {
      const d = this.#desktopMediaQuery.matches;
      this.classList.toggle('article-toc--desktop', d);
      this.classList.toggle('article-toc--mobile', !d);
      this.groupElements.forEach((details) => {
        details.open = d;
      });
    };
    this.#desktopMediaQuery.addEventListener('change', this.#handleViewportChange);

    this.observer = new IntersectionObserver(
      (entries) => {
        let topEntry = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!topEntry || entry.boundingClientRect.top < topEntry.boundingClientRect.top) {
            topEntry = entry;
          }
        }
        if (!topEntry) return;

        const link = this.headingMap.get(topEntry.target.id);
        if (!link || link === this.activeLink) return;

        this.activeLink?.classList.remove('is-active');
        link.classList.add('is-active');
        this.activeLink = link;
      },
      { rootMargin: '-80px 0px -65% 0px', threshold: 0 }
    );
    headings.forEach((h) => this.observer.observe(h));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.#handleViewportChange) {
      this.#desktopMediaQuery.removeEventListener('change', this.#handleViewportChange);
    }
  }

  #groupHeadings(headings) {
    const items = [];
    let currentGroup = null;
    headings.forEach((heading) => {
      if (heading.tagName === 'H2') {
        currentGroup = { heading, children: [], isGroup: false };
        items.push(currentGroup);
      } else if (currentGroup) {
        currentGroup.children.push(heading);
        currentGroup.isGroup = true;
      } else {
        items.push({ heading, children: [], isGroup: false });
      }
    });
    return items;
  }

  #getCaretHtml() {
    const tpl = this.querySelector('[data-toc-caret]');
    if (!tpl) return '';
    const wrapper = document.createElement('div');
    wrapper.appendChild(tpl.content.cloneNode(true));
    return wrapper.innerHTML;
  }

  #renderPlainItem(heading) {
    const li = document.createElement('li');
    if (heading.tagName === 'H3') li.classList.add('article-toc__item--sub');

    const link = document.createElement('a');
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent;

    this.headingMap.set(heading.id, link);
    li.appendChild(link);
    return li;
  }

  #renderGroup({ heading, children }, caretHtml, desktop) {
    const li = document.createElement('li');
    li.classList.add('article-toc__item-group');

    const childrenHtml = children
      .map(
        (child) =>
          `<li><a href="#${ArticleTableOfContents.escapeAttr(child.id)}">${ArticleTableOfContents.escapeHtml(child.textContent)}</a></li>`
      )
      .join('');

    const openAttr = desktop ? ' open=""' : '';

    const tmp = document.createElement('div');
    tmp.innerHTML =
      `<details is="accordion-disclosure" class="article-toc__details"${openAttr}>` +
      `<summary class="article-toc__summary">` +
      `<a href="#${ArticleTableOfContents.escapeAttr(heading.id)}" class="article-toc__summary-link">${ArticleTableOfContents.escapeHtml(heading.textContent)}</a>` +
      caretHtml +
      `</summary>` +
      `<ol class="article-toc__sublist">${childrenHtml}</ol>` +
      `</details>`;

    const details = tmp.firstElementChild;
    const summaryLink = details.querySelector('.article-toc__summary-link');
    summaryLink.addEventListener('click', (event) => event.stopPropagation());

    this.headingMap.set(heading.id, summaryLink);
    const subLinks = details.querySelectorAll('.article-toc__sublist > li > a');
    children.forEach((child, i) => {
      this.headingMap.set(child.id, subLinks[i]);
    });

    details.addEventListener('toggle', () => this.#handleGroupToggle(details));
    this.groupElements.push(details);

    li.appendChild(details);
    return li;
  }

  #handleGroupToggle(details) {
    if (!details.open) return;
    if (this.#desktopMediaQuery.matches) return;
    if (!this.closeOthers) return;

    this.groupElements.forEach((other) => {
      if (other !== details && other.open) other.open = false;
    });
  }

  static escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  static escapeAttr(value) {
    return ArticleTableOfContents.escapeHtml(value).replace(/"/g, '&quot;');
  }
}

customElements.define('article-toc', ArticleTableOfContents);
